import { keyBy } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import { ApiResponse, fetchApi } from "../../utils/api";
import { Entity } from "../../utils/constants";
import { DiscordChannel } from "../../utils/types";
import useMapCenter from "../../utils/useMapCenter";
import { useLoading } from "../loading";
import { LoadingKey } from "../loading/LoadingProvider";

interface ChannelsMap {
  [installId: string]: DiscordChannel;
}

type ContextValue = {
  channels: ChannelsMap;
  requestChannel: (
    values: DiscordChannelValues
  ) => Promise<ApiResponse<DiscordChannel>>;
  fetchChannels: () => Promise<ChannelsMap | undefined>;
};

const DiscordChannelsContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

export const DiscordChannelsProvider: React.FC<Props> = (props) => {
  const { addLoader, removeLoader } = useLoading();
  const { pathname } = useLocation();
  const centerInH3HexRes4 = useMapCenter((state) => state.centerInH3HexRes4);
  const [channels, setChannels] = useState<ChannelsMap>({});

  const fetchChannels = useCallback(async () => {
    if (!centerInH3HexRes4) return;

    addLoader(LoadingKey.discordChannels);

    const res = await fetchApi(`/api/discord/${centerInH3HexRes4}/channels`);

    const apiResponse: ApiResponse<DiscordChannel[]> = await res.json();

    let newChannels = {};

    if (apiResponse.status) {
      newChannels = keyBy(apiResponse.data, "linked_id");

      setChannels(newChannels);
    }

    removeLoader(LoadingKey.discordChannels);

    return newChannels;
  }, [addLoader, centerInH3HexRes4, removeLoader]);

  const detailsPageMatch = matchPath<{
    tab: string; // hotspots, locations, hexes, contacts, installs
    id: string;
  }>(pathname, {
    path: ["/:tab/:id", "/:tab"]
  });

  useEffect(() => {
    if (detailsPageMatch?.params.tab !== "hexes") return;

    fetchChannels();
  }, [detailsPageMatch?.params.tab, fetchChannels]);

  const requestChannel = useCallback(
    async (values: DiscordChannelValues) => {
      const response = await fetchApi("/api/discord/channel", {
        method: "POST",
        body: JSON.stringify(values)
      });

      const apiResponse: ApiResponse<DiscordChannel> = await response.json();

      if (apiResponse.status) {
        await fetchChannels();

        const newChannel = apiResponse.data;

        setChannels((prevChannels) => ({
          ...prevChannels,
          [values.linked_id]: newChannel
        }));
      }

      return apiResponse;
    },
    [fetchChannels]
  );

  const value = useMemo(
    () => ({
      channels,
      fetchChannels,
      requestChannel
    }),
    [channels, fetchChannels, requestChannel]
  );

  return <DiscordChannelsContext.Provider value={value} {...props} />;
};

export const useDiscordChannels = (): ContextValue => {
  const context = useContext(DiscordChannelsContext);
  if (context === undefined) {
    throw new Error(
      "useDiscordChannels must be used within an DiscordChannelsProvider"
    );
  }
  return context;
};

//
// Utils
//

interface DiscordChannelValues {
  name: string;
  type: Entity;
  linked_id: string;
  h3_index_r4: string;
}
